import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import {
  Button,
  Input,
  Textarea,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServiceAdvancedStrategy,
  useServiceAdvancedEnabledSites,
  useCreateServiceAdvancedStrategy,
  useUpdateServiceAdvancedStrategy,
  ADVANCED_TYPE_ILLEGAL_DOWNLOAD,
  type AdvancedStrategy,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './IllegalDownloadForm.module.css';

const NAME_MAX = 26;
const DESC_MAX = 200;
const EXT_MAX = 200;
const TYPE = ADVANCED_TYPE_ILLEGAL_DOWNLOAD;

/** Action policies for Illegal Download (no delay/redirect — Block or By-pass). */
const ACTIONS = [
  { value: 'Block', label: 'Block' },
  { value: 'Bypass', label: 'By-pass' },
] as const;

/** MIME types selectable for the restricted list. Only "application/json" is
 *  confirmed from a payload; the rest are common downloadable types. */
const MIME_TYPES = [
  'application/json',
  'application/xml',
  'application/octet-stream',
  'application/pdf',
  'application/zip',
  'application/x-msdownload',
  'application/x-sh',
  'text/html',
  'text/plain',
  'image/jpeg',
  'image/png',
] as const;

/** The Illegal Download Protection (type 11) per-type config. */
interface IllegalDownloadConfig {
  action: string;
  block_value: number;
  enable_file_extension: boolean;
  enable_max_length: boolean;
  enable_mime_types: boolean;
  file_extension: string;
  max_length: number;
  mime_types: string[];
}

interface FormState extends IllegalDownloadConfig {
  name: string;
  description: string;
  sites: number[];
}

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  sites: [],
  action: 'Block',
  block_value: 418,
  enable_file_extension: true,
  enable_max_length: true,
  enable_mime_types: true,
  file_extension: '',
  max_length: 1000,
  mime_types: [],
});

/**
 * Add / Edit an Illegal Download Protection rule (AI-WAF → Advanced Protection,
 * waf_advanced_strategy type 11). Blocks downloads by size / extension / MIME type.
 * Builds the per-type `config`; the BE adds the common envelope keys. Routes:
 * aiwaf/advanced/illegal-download/new + /:id.
 */
export function IllegalDownloadForm() {
  const navigate = useNavigate();
  const { serviceId, id } = useParams<{ serviceId: string; id: string }>();
  const isEdit = !!id;
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;


  const { data: existing, isLoading: loadingExisting } = useServiceAdvancedStrategy<IllegalDownloadConfig>(
    serviceId,
    TYPE,
    id,
  );
  const { data: sites = [] } = useServiceAdvancedEnabledSites(serviceId);

  const [form, setForm] = useState<FormState>(emptyForm);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!existing) return;
    const c = existing.config ?? ({} as IllegalDownloadConfig);
    setForm({
      name: existing.name ?? '',
      description: existing.description ?? '',
      sites: existing.sites ?? [],
      action: c.action || 'Block',
      block_value: c.block_value ?? 418,
      enable_file_extension: c.enable_file_extension ?? false,
      enable_max_length: c.enable_max_length ?? false,
      enable_mime_types: c.enable_mime_types ?? false,
      file_extension: c.file_extension ?? '',
      max_length: c.max_length ?? 1000,
      mime_types: Array.isArray(c.mime_types) ? c.mime_types : [],
    });
  }, [existing]);

  const siteSet = useMemo(() => new Set(form.sites), [form.sites]);
  const toggleSite = (sid: number) =>
    set('sites', siteSet.has(sid) ? form.sites.filter((x) => x !== sid) : [...form.sites, sid]);

  const addMime = (m: string) => {
    if (!form.mime_types.includes(m)) set('mime_types', [...form.mime_types, m]);
  };
  const removeMime = (m: string) => set('mime_types', form.mime_types.filter((x) => x !== m));

  const create = useCreateServiceAdvancedStrategy<IllegalDownloadConfig>(serviceId ?? '', TYPE, {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'illegal_download_rule', 'create');
      show({ tone: 'success', title: 'Illegal Download rule created' });
      navigate(`${base}/aiwaf/advanced?tab=illegal-download`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateServiceAdvancedStrategy<IllegalDownloadConfig>(serviceId ?? '', TYPE, id ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'illegal_download_rule', 'edit');
      show({ tone: 'success', title: 'Illegal Download rule saved' });
      navigate(`${base}/aiwaf/advanced?tab=illegal-download`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });
  const saving = create.isPending || update.isPending;

  const nameValid = form.name.trim().length > 0 && form.name.trim().length <= NAME_MAX;
  // Each enabled trigger must carry a value; at least one trigger must be on.
  const sizeOk = !form.enable_max_length || form.max_length > 0;
  const extOk = !form.enable_file_extension || form.file_extension.trim().length > 0;
  const mimeOk = !form.enable_mime_types || form.mime_types.length > 0;
  const anyTrigger = form.enable_max_length || form.enable_file_extension || form.enable_mime_types;
  const actionValid = form.action !== 'Block' ? true : form.block_value > 0;
  const canSubmit =
    nameValid && anyTrigger && sizeOk && extOk && mimeOk && actionValid && !saving;

  const submit = () => {
    const payload: AdvancedStrategy<IllegalDownloadConfig> = {
      type: TYPE,
      name: form.name.trim(),
      description: form.description.trim(),
      sites: form.sites,
      config: {
        action: form.action,
        block_value: form.block_value,
        enable_file_extension: form.enable_file_extension,
        enable_max_length: form.enable_max_length,
        enable_mime_types: form.enable_mime_types,
        file_extension: form.file_extension.trim(),
        max_length: form.max_length,
        mime_types: form.mime_types,
      },
    };
    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  };

  if (isEdit && loadingExisting) {
    return (
      <Container size="xl">
        <Skeleton className={styles.skelForm} />
      </Container>
    );
  }

  const availableMimes = MIME_TYPES.filter((m) => !form.mime_types.includes(m));

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'AI-WAF' },
              { label: 'Advanced Protection', slug: 'aiwaf/advanced' },
              { label: isEdit ? 'Edit Illegal Download Rule' : 'Illegal Download Protection Rule' },
            ]}
          />
        }
        title="Illegal Download Protection Rule"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
          <Card flat>
            <CardHeader>
              <CardTitle>General</CardTitle>
            </CardHeader>
            <CardBody>
            <div className={styles.fieldGrid}>
              <Field
                label="Name"
                required
                description={`1-${NAME_MAX} characters. Valid: 0-9, A-Z, a-z, space, . _ - ( ) * +`}
              >
                {(props) => (
                  <Input
                    {...props}
                    value={form.name}
                    maxLength={NAME_MAX}
                    placeholder="Rule name"
                    onChange={(e) => set('name', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Description" description={`Up to ${DESC_MAX} characters.`}>
                {(props) => (
                  <div>
                    <Textarea
                      {...props}
                      value={form.description}
                      maxLength={DESC_MAX}
                      placeholder="Description"
                      onChange={(e) => set('description', e.target.value)}
                    />
                    <div className={styles.charCount}>
                      {form.description.length}/{DESC_MAX}
                    </div>
                  </div>
                )}
              </Field>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Triggering Condition</CardTitle>
            </CardHeader>
            <CardBody>
            <div className={styles.fieldGridLg}>
              {/* File Size Limit */}
              <Trigger
                title="File Size Limit"
                description="Enter an integer greater than 0 to define the maximum downloadable file size."
                enabled={form.enable_max_length}
                onToggle={(v) => set('enable_max_length', v)}
              >
                <div className={styles.inlineCenter}>
                  <Input
                    type="number"
                    min={1}
                    value={String(form.max_length)}
                    className={styles.sizeInput}
                    onChange={(e) => set('max_length', Number(e.target.value))}
                  />
                  <span className={styles.muted}>bytes</span>
                </div>
              </Trigger>

              {/* Restricted Filename Extension */}
              <Trigger
                title="Restricted Filename Extension"
                description="File extensions that can't be downloaded — separate with commas."
                enabled={form.enable_file_extension}
                onToggle={(v) => set('enable_file_extension', v)}
              >
                <Textarea
                  value={form.file_extension}
                  maxLength={EXT_MAX}
                  placeholder="exe,php,zip,rar,7z,sh,py"
                  onChange={(e) => set('file_extension', e.target.value)}
                />
              </Trigger>

              {/* Restricted MIME Types */}
              <Trigger
                title="Restricted MIME Types"
                description="MIME types of files that can't be downloaded."
                enabled={form.enable_mime_types}
                onToggle={(v) => set('enable_mime_types', v)}
              >
                <div className={styles.mimeWrap}>
                  {form.mime_types.length > 0 && (
                    <div className={styles.chipRow}>
                      {form.mime_types.map((m) => (
                        <span key={m} className={styles.chip}>
                          {m}
                          <X
                            size={13}
                            className={styles.removeIcon}
                            onClick={() => removeMime(m)}
                            aria-label={`Remove ${m}`}
                          />
                        </span>
                      ))}
                    </div>
                  )}
                  <Select
                    value=""
                    disabled={availableMimes.length === 0}
                    onValueChange={(v) => v && addMime(v)}
                  >
                    <SelectTrigger className={styles.selectLg}>
                      <SelectValue placeholder="Select a MIME type to add" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableMimes.map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </Trigger>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Action</CardTitle>
              <CardDescription>Block denies the request with the status code below; By-pass forwards it.</CardDescription>
            </CardHeader>
            <CardBody>
            <div className={styles.actionGrid}>
              <Field label="Action" required>
                {() => (
                  <Select value={form.action} onValueChange={(v) => set('action', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIONS.map((a) => (
                        <SelectItem key={a.value} value={a.value}>
                          {a.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              {form.action === 'Block' && (
                <Field label="Response Status Code" required>
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={100}
                      max={599}
                      value={String(form.block_value)}
                      className={styles.numField}
                      onChange={(e) => set('block_value', Number(e.target.value))}
                    />
                  )}
                </Field>
              )}
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Apply to</CardTitle>
              <CardDescription>Select the websites this rule protects.</CardDescription>
            </CardHeader>
            <CardBody>
            {sites.length === 0 ? (
              <div className={styles.muted}>
                No protection-enabled websites available.
              </div>
            ) : (
              <div className={styles.siteList}>
                {sites.map((s) => (
                  <label key={s.id} className={styles.siteLabel}>
                    <Checkbox checked={siteSet.has(s.id)} onCheckedChange={() => toggleSite(s.id)} />
                    <span>{s.domain}</span>
                  </label>
                ))}
              </div>
            )}
            </CardBody>
          </Card>

          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => navigate(`${base}/aiwaf/advanced?tab=illegal-download`)}>
              Cancel
            </Button>
            <Button disabled={!canSubmit} onClick={submit}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
    </Container>
  );
}

/** A triggering-condition block: title + description + an Enable checkbox gating
 *  its field. */
function Trigger({
  title,
  description,
  enabled,
  onToggle,
  children,
}: {
  title: string;
  description: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className={styles.triggerTitle}>
        {title} <span className={styles.required}>*</span>
      </div>
      <div className={styles.triggerDesc}>
        {description}
      </div>
      <div className={styles.triggerBox}>
        <label className={styles.checkLabel}>
          <Checkbox checked={enabled} onCheckedChange={(v) => onToggle(!!v)} />
          <span>Enable</span>
        </label>
        {children}
      </div>
    </div>
  );
}
